/**
 * React Query hooks for Plugin Settings
 *
 * Provides queries for managing plugin settings with automatic cache invalidation.
 *
 * Usage:
 * ```typescript
 * import { usePluginSettingsQuery } from '@/lib/platform/hooks/queries/use-plugin-settings-query';
 *
 * function PluginsPage() {
 *   const organizationId = 'my-org-id';
 *   const { data: plugins, isLoading, isPluginEnabled } = usePluginSettingsQuery(organizationId);
 *
 *   if (isPluginEnabled('ai-insights')) {
 *     // Plugin is enabled
 *   }
 * }
 * ```
 */

'use client';

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from './query-keys';
import { useMemo } from 'react';
import { fetchWithBypass } from '@/lib/platform/utils/fetch';

export interface PluginSetting {
  id: string;
  organizationId: string;
  manifest: {
    id: string;
    name: string;
    version: string;
    [key: string]: unknown;
  };
  enabled: boolean;
  health?: {
    status: 'healthy' | 'degraded' | 'unhealthy';
    message?: string;
    lastCheck?: Date;
  };
  settings?: Record<string, unknown>;
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * Fetch plugin settings for an organization
 */
async function fetchPluginSettings(organizationId: string): Promise<PluginSetting[]> {
  const response = await fetchWithBypass(`/api/plugin-settings?organizationId=${organizationId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch plugin settings');
  }

  const result = await response.json();
  return result.data || [];
}

/**
 * Hook to fetch and cache plugin settings for an organization
 *
 * Features:
 * - Automatic caching (5 minutes stale time)
 * - Background refetching
 * - Automatic retries on failure
 * - Helper function to check if a plugin is enabled
 */
export function usePluginSettingsQuery(organizationId?: string) {
  const query = useQuery({
    queryKey: queryKeys.pluginSettings.org(organizationId!),
    queryFn: () => fetchPluginSettings(organizationId!),
    enabled: !!organizationId, // Only fetch if organizationId is provided
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Helper function to check if a plugin is enabled
  const isPluginEnabled = useMemo(() => {
    return (pluginId: string): boolean => {
      const plugins = query.data || [];
      const plugin = plugins.find((p) => p.manifest.id === pluginId);
      return plugin?.enabled === true;
    };
  }, [query.data]);

  return {
    ...query,
    isPluginEnabled,
  };
}
